import React, { useCallback, useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { getBirthdayRequestApi, payBirthdayRequestApi } from "../../api/api";
import { resolveUrl } from "../reservas/components/ReservaShared";
import { formatDeadline, formatLongDate, formatMoney, recallRequestClub, resolveRequestState } from "./birthdayModel";
import { ResultShell } from "./BirthdaySteps";
import "../reservas/reservas.css";
import "./cumpleanos.css";

const Row = ({ label, value, total }) => (
  <div className={`bday-row${total ? " total" : ""}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const Summary = ({ view, children }) => (
  <div className="bday-summary">
    <Row label="Club" value={view.club?.name} />
    <Row label="Paquete" value={view.package?.name} />
    <Row label="Fecha" value={`${formatLongDate(view.date)}, ${view.slotStart} hs`} />
    {view.details?.guests ? <Row label="Invitados" value={view.details.guests} /> : null}
    {children}
  </div>
);

/** Page of the link the club emails to the customer (/cumpleanos/solicitud/:token). */
const BirthdayRequestPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", view: null, error: "" });
  const [reloadKey, setReloadKey] = useState(0);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");

  useEffect(() => {
    let active = true;
    setState({ status: "loading", view: null, error: "" });
    getBirthdayRequestApi(token)
      .then((response) => {
        if (!active) return;
        setState({ status: "ready", view: response?.data?.info || null, error: "" });
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 404) setState({ status: "notfound", view: null, error: "" });
        else {
          setState({
            status: "error",
            view: null,
            error: err?.response?.data?.message || "No pudimos cargar tu solicitud.",
          });
        }
      });
    return () => {
      active = false;
    };
  }, [token, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const pay = useCallback(async () => {
    setPaying(true);
    setPayError("");
    try {
      const response = await payBirthdayRequestApi(token);
      const href = response?.data?.info?.paymentHref;
      if (!href) throw new Error("missing paymentHref");
      try {
        // The shared success/cancel pages read this key after the gateway returns.
        sessionStorage.setItem(
          "reservation:last",
          JSON.stringify({ flow: "canchas", reservationCode: state.view?.reservation?.reservationCode })
        );
      } catch (_) {
        // Storage can be unavailable (private mode); the redirect still works.
      }
      window.location.href = resolveUrl(href);
    } catch (err) {
      setPayError(
        err?.response?.data?.message || "No pudimos iniciar el pago. Probá de nuevo en un momento."
      );
      // A 409 means the deadline passed or it was already paid: show the real state.
      if (err?.response?.status === 409) reload();
      setPaying(false);
    }
  }, [token, state.view, reload]);

  if (state.status === "loading") {
    return (
      <div className="reservas-root bday">
        <div className="bday-state">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if (state.status === "notfound" || (state.status === "ready" && !state.view)) {
    return (
      <div className="reservas-root bday">
        <ResultShell icon="search" tone="neutral" title="No encontramos esta solicitud">
          <p>El link no es válido. Revisá que lo hayas copiado completo desde el email.</p>
          <div className="bday-state-actions">
            <button type="button" className="r-btn-soft" onClick={() => navigate("/canchas")}>
              Ver canchas
            </button>
          </div>
        </ResultShell>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="reservas-root bday">
        <ResultShell icon="alert" tone="danger" title="No pudimos cargar tu solicitud">
          <p>{state.error}</p>
          <div className="bday-state-actions">
            <button type="button" className="r-btn-primary" onClick={reload}>
              Reintentar
            </button>
          </div>
        </ResultShell>
      </div>
    );
  }

  const view = state.view;
  const screen = resolveRequestState(view);
  const clubId = view.clubId || recallRequestClub(token);
  const goFlow = () => navigate(clubId ? `/canchas/${clubId}/cumpleanos` : "/canchas");

  if (screen === "pending") {
    return (
      <div className="reservas-root bday">
        <ResultShell icon="clock" tone="neutral" title="El club está revisando tu solicitud">
          <p>
            {view.customerName ? `${view.customerName}, ` : ""}el club te responde antes del{" "}
            <strong>{formatDeadline(view.respondBy)}</strong>. Todavía no pagaste nada y el horario no está reservado.
          </p>
          <Summary view={view} />
        </ResultShell>
      </div>
    );
  }

  if (screen === "payable") {
    return (
      <div className="reservas-root bday">
        <ResultShell title="El club aceptó tu cumple">
          {view.message && <p>{view.message}</p>}
          <Summary view={view}>
            <Row label="Total" value={formatMoney(view.price)} total />
            <Row label="Seña" value={formatMoney(view.depositAmount)} />
            <Row label="Saldo a pagar en el club" value={formatMoney(view.balanceDue)} />
          </Summary>
          <p>
            Tenés tiempo hasta el <strong>{formatDeadline(view.reservation?.expiresAt)}</strong> para pagar la seña. Si
            no, el horario se libera.
          </p>
          {view.policy?.text && <div className="bday-policy">{view.policy.text}</div>}
          {payError && <Alert variant="danger">{payError}</Alert>}
          <div className="bday-state-actions">
            <button type="button" className="r-btn-primary" disabled={paying} onClick={pay}>
              {paying ? <Spinner animation="border" size="sm" /> : `Pagar seña de ${formatMoney(view.depositAmount)}`}
            </button>
          </div>
        </ResultShell>
      </div>
    );
  }

  if (screen === "confirmed") {
    return (
      <div className="reservas-root bday">
        <ResultShell title="¡Listo, el cumple está confirmado!">
          <Summary view={view}>
            {view.reservation?.reservationCode && <Row label="Código" value={view.reservation.reservationCode} />}
            <Row label="A pagar en el club" value={formatMoney(view.reservation?.balanceDue ?? view.balanceDue)} total />
          </Summary>
          <p>Te esperamos. El saldo se paga en el club el día del cumple.</p>
        </ResultShell>
      </div>
    );
  }

  if (screen === "rejected") {
    return (
      <div className="reservas-root bday">
        <ResultShell icon="x" tone="danger" title="El club no pudo aceptar tu solicitud">
          {view.rejectReason && <p>Motivo: {view.rejectReason}</p>}
          <p>No se cobró nada. Podés pedir otra fecha.</p>
          <div className="bday-state-actions">
            <button type="button" className="r-btn-primary" onClick={goFlow}>
              Pedir otra fecha
            </button>
          </div>
        </ResultShell>
      </div>
    );
  }

  if (screen === "expired" || screen === "cancelled") {
    const expired = screen === "expired";
    return (
      <div className="reservas-root bday">
        <ResultShell icon="clock" tone="neutral" title={expired ? "Este link de pago venció" : "Esta solicitud fue cancelada"}>
          <p>
            {expired
              ? "Pasó el plazo y el horario se liberó. No se cobró nada."
              : "No hay nada para pagar en esta solicitud."}{" "}
            Si todavía querés festejar en el club, mandá otra solicitud.
          </p>
          <div className="bday-state-actions">
            <button type="button" className="r-btn-primary" onClick={goFlow}>
              Mandar otra solicitud
            </button>
          </div>
        </ResultShell>
      </div>
    );
  }

  // Unknown or future status (e.g. a proposed time): show the data without actions instead of breaking.
  return (
    <div className="reservas-root bday">
      <ResultShell icon="info" tone="neutral" title="Tu solicitud está en curso">
        <p>El club todavía está resolviendo tu pedido. Volvé a mirar este link en un rato.</p>
        <Summary view={view} />
        <div className="bday-state-actions">
          <button type="button" className="r-btn-soft" onClick={reload}>
            Actualizar
          </button>
        </div>
      </ResultShell>
    </div>
  );
};

export default BirthdayRequestPage;
